import {
  EvaluationMethodology,
  EvaluationRun,
  EvaluationRunDataset,
  EvaluationRunMetric,
  EvaluationRunStatus,
  EvaluationRunTestCase,
  EvaluationSettings,
} from "../models/types";
import queryClient, { QueryKey } from "../state/QueryStore";
import { ListResponse } from "../utilities/ApiResponseHelper";
import {
  httpDeleteAuthenticated,
  httpGetAuthenticated,
  httpPatchAuthenticated,
  httpPostAuthenticated,
} from "./ApiService";

const evaluationRunEndpoints = {
  evaluations: () => "/evaluation_runs",
  evaluation: (evaluationId: string) => `/evaluation_runs/${evaluationId}`,
  evaluationTestCases: (evaluationId: string) => `/evaluation_runs/${evaluationId}/test_cases`,
  evaluationAddTestCase: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/add_test_case?fario_data=true`,
  evaluationRemoveTestCase: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/remove_test_case?fairo_data=true`,
  evaluationUpdateTestCaseReason: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/update_test_case_reason`,
  evaluationDatasets: (evaluationId: string) => `/evaluation_runs/${evaluationId}/datasets`,
  evaluationAddDataset: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/add_dataset?fario_data=true`,
  evaluationRemoveDataset: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/remove_dataset?fairo_data=true`,
  evaluationUpdateDatasetReason: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/update_dataset_reason`,
  evaluationMetrics: (evaluationId: string) => `/evaluation_runs/${evaluationId}/metrics`,
  evaluationAddMetric: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/add_metric?fario_data=true`,
  evaluationRemoveMetric: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/remove_metric?fairo_data=true`,
  evaluationUpdateMetricReason: (evaluationId: string) =>
    `/evaluation_runs/${evaluationId}/update_metric_reason`,
};

export const getEvaluationRun = (evaluationId: string) => {
  return httpGetAuthenticated<EvaluationRun>(evaluationRunEndpoints.evaluation(evaluationId));
};

export const getEvaluationRuns = (params: any) => {
  return httpGetAuthenticated<ListResponse<EvaluationRun>>(evaluationRunEndpoints.evaluations(), {
    params,
  });
};

export const addEvaluationRun = async (payload: {
  name: string;
  description?: string;
  evaluation_methodology: EvaluationMethodology | null;
  evaluated_asset: string;
  settings: EvaluationSettings;
  framework: string;
  evaluated_controls: string[];
  related_test_cases?: string[];
  related_use_case: string | null;
}) => {
  const { data } = await httpPostAuthenticated<EvaluationRun>(
    evaluationRunEndpoints.evaluations(),
    payload
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRun] });
  return data;
};

export const editEvaluationRun = async (
  evaluationId: string,
  params: { name?: string; description?: string; related_test_cases?: string[] }
) => {
  const { data } = await httpPatchAuthenticated(
    evaluationRunEndpoints.evaluation(evaluationId),
    params
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRun] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.TestCase] });

  return data;
};

export const deleteEvaluationRun = async (evaluationId: string) => {
  const resp = await httpDeleteAuthenticated(evaluationRunEndpoints.evaluation(evaluationId));
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRun] });
  return resp;
};

export const getEvaluationRunTestCases = async (evaluationId: string) => {
  return httpGetAuthenticated<ListResponse<EvaluationRunTestCase>>(
    evaluationRunEndpoints.evaluationTestCases(evaluationId)
  );
};

export const addTestCaseToEvaluationRun = async (
  testCaseId: string,
  evaluationRunId: string,
  reason: string | null
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationAddTestCase(evaluationRunId),
    { test_case_id: testCaseId, reason: reason }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunTestCase] });
  return resp;
};

export const removeTestCaseFromEvaluationRun = async (
  testCaseId: string,
  evaluationRunId: string
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationRemoveTestCase(evaluationRunId),
    {
      test_case_id: testCaseId,
    }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunTestCase] });
  return resp;
};

export const updateTestCaseReason = async (
  testCaseId: string,
  evaluationRunId: string,
  reason: string | null
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationUpdateTestCaseReason(evaluationRunId),
    { test_case_id: testCaseId, reason: reason }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunTestCase] });
  return resp;
};

export const getEvaluationRunDatasets = async (evaluationId: string) => {
  return httpGetAuthenticated<ListResponse<EvaluationRunDataset>>(
    evaluationRunEndpoints.evaluationDatasets(evaluationId)
  );
};

export const addDatasetToEvaluationRun = async (
  datasetId: string,
  evaluationRunId: string,
  reason: string | null
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationAddDataset(evaluationRunId),
    { dataset_id: datasetId, reason: reason }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunDataset] });
  return resp;
};

export const removeDatasetFromEvaluationRun = async (
  datasetId: string,
  evaluationRunId: string
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationRemoveDataset(evaluationRunId),
    {
      dataset_id: datasetId,
    }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunDataset] });
  return resp;
};

export const updateDatasetReason = async (
  datasetId: string,
  evaluationRunId: string,
  reason: string | null
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationUpdateDatasetReason(evaluationRunId),
    { dataset_id: datasetId, reason: reason }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunDataset] });
  return resp;
};

export const getEvaluationRunMetrics = async (evaluationId: string) => {
  return httpGetAuthenticated<ListResponse<EvaluationRunMetric>>(
    evaluationRunEndpoints.evaluationMetrics(evaluationId)
  );
};

export const addMetricToEvaluationRun = async (
  metricId: string,
  evaluationRunId: string,
  reason: string | null
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationAddMetric(evaluationRunId),
    { metric_id: metricId, reason: reason }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunMetric] });
  return resp;
};

export const removeMetricFromEvaluationRun = async (metricId: string, evaluationRunId: string) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationRemoveMetric(evaluationRunId),
    {
      metric_id: metricId,
    }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunMetric] });
  return resp;
};

export const updateMetricReason = async (
  metricId: string,
  evaluationRunId: string,
  reason: string | null
) => {
  const resp = await httpPostAuthenticated(
    evaluationRunEndpoints.evaluationUpdateMetricReason(evaluationRunId),
    { metric_id: metricId, reason: reason }
  );
  queryClient.invalidateQueries({ queryKey: [QueryKey.EvaluationRunMetric] });
  return resp;
};
